import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Save,
  RotateCcw,
  FileText,
  ChevronDown,
  ChevronUp,
  Send,
  Lock,
  X,
} from 'lucide-react';
import {
  questionnaireData as fallbackQuestionnaireData,
  isQuestionAnswered,
} from '../data/config';
import { isQuestionVisibleDsl } from '../data/questionnaireHelpers';
import Tooltip from './Tooltip';
import ConfirmationModal from './ConfirmationModal';

// Compat shim: in this file, `isQuestionVisible(q, responses)` is the old
// hardcoded function's signature. We route it through the DSL evaluator
// so questions authored in the editor (ticket 06) with `visibleIf` rules
// just work, and questions without rules stay visible.
const isQuestionVisible = isQuestionVisibleDsl;

// --- ADDITION: Custom hook to get window size ---
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize(); // Call handler right away so state gets updated with initial window size
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

const QuestionForm = ({
  paper,
  questionnaire: questionnaireProp,
  assignedQuestionIds,
  responses,
  onResponsesUpdate,
  userId,
  isSubmitted,
  onSubmit,
}) => {
  // Ticket 05: questionnaire now comes from Firestore via MainApp. It's
  // passed in the legacy shape ({ questionnaire: [{ section_title, ... }] })
  // so the rest of this file didn't have to be rewritten. Fall back to
  // the module-level constant if the prop is missing — defensive, covers
  // the case where Firestore read fails.
  const questionnaireData = questionnaireProp || fallbackQuestionnaireData;

  const [activeSectionTitle, setActiveSectionTitle] = useState(
    () =>
      (questionnaireData.questionnaire[0] &&
        questionnaireData.questionnaire[0].section_title) ||
      '',
  );
  const [currentResponses, setCurrentResponses] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [autosaveError, setAutosaveError] = useState('');
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [validationError, setValidationError] = useState(null);
  const [isValidationErrorVisible, setValidationErrorVisible] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);

  // --- ADDITION: Get window height and define threshold for responsive UI ---
  const { height: windowHeight } = useWindowSize();
  const SHORT_SCREEN_THRESHOLD = 750; // Pixels
  const isShortScreen = windowHeight < SHORT_SCREEN_THRESHOLD;

  const autosaveTimerRef = useRef(null);
  const hasUnsavedChangesRef = useRef(false);
  const scrollContainerRef = useRef(null);

  // Flatten the loaded questionnaire to the list the rest of this file
  // expects: every question (including group sub_questions) with its
  // section title stashed on it. Replaces the old getQuestionsForPaper.
  //
  // Ticket 08: if assignedQuestionIds is provided (reviewer has an
  // assignment doc for this paper), FILTER leaf questions down to that
  // subset. Groups are kept iff at least one of their leaf descendants
  // is in the subset. An absent assignedQuestionIds prop means "show
  // everything" — useful for admin previews and the phase-1 fallback
  // when the assignments collection hasn't been populated yet.
  const allQuestions = useMemo(() => {
    const subsetActive =
      Array.isArray(assignedQuestionIds) && assignedQuestionIds.length > 0;
    const subsetSet = subsetActive ? new Set(assignedQuestionIds) : null;

    const isLeafInSubset = (node) =>
      node.type !== 'group' && (!subsetActive || subsetSet.has(node.id));

    const groupHasSubsetLeaf = (group) => {
      if (!subsetActive) return true;
      const stack = [...(group.sub_questions || [])];
      while (stack.length > 0) {
        const cur = stack.pop();
        if (cur.type === 'group') {
          stack.push(...(cur.sub_questions || []));
        } else if (subsetSet.has(cur.id)) {
          return true;
        }
      }
      return false;
    };

    const out = [];
    const walk = (nodes, section) => {
      for (const node of nodes || []) {
        if (node.type === 'group') {
          if (groupHasSubsetLeaf(node)) {
            out.push({ ...node, category: section.section_title });
            walk(node.sub_questions, section);
          }
        } else if (isLeafInSubset(node)) {
          out.push({ ...node, category: section.section_title });
        }
      }
    };
    (questionnaireData.questionnaire || []).forEach((section) =>
      walk(section.questions, section),
    );
    return out;
  }, [questionnaireData, assignedQuestionIds]);

  const nonGroupQuestions = useMemo(
    () => allQuestions.filter((q) => q.type !== 'group'),
    [allQuestions]
  );

  // --- DEFINITIVE FIX: Separated useEffect Hooks ---

  // Hook 1: Manages DATA SYNCHRONIZATION.
  // Its only job is to ensure the form's internal state (`currentResponses`)
  // is always in sync with the data passed from the parent (`responses`).
  // This is the critical fix that prevents data loss and state leakage.
  useEffect(() => {
    setCurrentResponses(responses);
  }, [responses]);

  // Hook 2: Manages INITIAL UI STATE (Question Expansion).
  // This runs only when a new paper is selected to set the initial view.
  // It does not run on every answer change, thus preventing auto-folding.
  useEffect(() => {
    if (isSubmitted) {
      setExpandedQuestions(new Set());
      return;
    }

    const initialExpanded = new Set();
    allQuestions.forEach((q) => {
      if (q.type === 'group') {
        const visibleSubQs = q.sub_questions.filter((subQ) =>
          isQuestionVisible(subQ, responses)
        );
        const isComplete = visibleSubQs.every((subQ) =>
          isQuestionAnswered(subQ.id, responses)
        );
        if (!isComplete) {
          initialExpanded.add(q.id);
        }
      } else if (!isQuestionAnswered(q.id, responses)) {
        initialExpanded.add(q.id);
      }
    });
    setExpandedQuestions(initialExpanded);
    // This hook intentionally runs only when a new paper's data is loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paper.id, isSubmitted]);

  // --- End of Fix ---

  // Live validation check when responses change
  useEffect(() => {
    if (validationError) {
      const visibleQuestions = nonGroupQuestions.filter((q) =>
        isQuestionVisible(q, currentResponses)
      );
      const stillUnanswered = visibleQuestions.filter(
        (q) => !isQuestionAnswered(q.id, currentResponses)
      );

      if (stillUnanswered.length > 0) {
        setValidationError(stillUnanswered);
      } else {
        setValidationError(null);
        setValidationErrorVisible(false);
      }
    }
  }, [currentResponses, nonGroupQuestions, validationError]);

  const performAutosave = async () => {
    if (!hasUnsavedChangesRef.current || isSubmitted) return;
    setAutoSaveStatus('saving');
    try {
      await onResponsesUpdate(currentResponses);
      hasUnsavedChangesRef.current = false;
      setAutosaveError('');
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (err) {
      setAutoSaveStatus('idle');
      setAutosaveError(err?.message || 'Autosave failed. Please try saving again.');
    }
  };

  useEffect(() => {
    if (hasUnsavedChangesRef.current) {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = setTimeout(performAutosave, 2000);
    }
    return () => clearTimeout(autosaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentResponses]);

  const getCleanedResponses = (responses) => {
    const newResponses = { ...responses };
    let wasChanged = true;

    while (wasChanged) {
      wasChanged = false;
      const currentIds = Object.keys(newResponses);

      for (const qId of currentIds) {
        const question = allQuestions.find((q) => q.id === qId);
        if (question && !isQuestionVisible(question, newResponses)) {
          delete newResponses[qId];
          wasChanged = true;
        }
      }
    }
    return newResponses;
  };

  const handleInputChange = (questionId, value) => {
    if (isSubmitted) return;

    let initialNewResponses;
    const question = allQuestions.find((q) => q.id === questionId);
    if (question && question.type === 'multi_select_mcq') {
      const currentSelection = currentResponses[questionId] || [];
      const newSelection = currentSelection.includes(value)
        ? currentSelection.filter((item) => item !== value)
        : [...currentSelection, value];
      initialNewResponses = { ...currentResponses, [questionId]: newSelection };
    } else {
      initialNewResponses = { ...currentResponses, [questionId]: value };
    }

    const finalResponses = getCleanedResponses(initialNewResponses);
    setCurrentResponses(finalResponses);

    hasUnsavedChangesRef.current = true;
    setAutosaveError('');
    if (autoSaveStatus === 'saved') setAutoSaveStatus('idle');
  };

  const toggleQuestionExpansion = (questionId) => {
    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      newSet.has(questionId)
        ? newSet.delete(questionId)
        : newSet.add(questionId);
      return newSet;
    });
  };

  const handleSave = () => {
    if (isSubmitted) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    performAutosave();
  };

  const handleReset = () => {
    if (isSubmitted) return;
    if (
      window.confirm(
        'Are you sure you want to clear all responses? This action cannot be undone.'
      )
    ) {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      setCurrentResponses({});
      onResponsesUpdate({});
      hasUnsavedChangesRef.current = false;
      setAutoSaveStatus('idle');
      setExpandedQuestions(new Set(nonGroupQuestions.map((q) => q.id)));
    }
  };

  const handleJumpToQuestion = (question) => {
    setActiveSectionTitle(question.category);
    const idParts = question.id.split('.');
    if (question.id.startsWith('8.') && idParts.length === 3) {
      const parentGroupId = `${idParts[0]}.${idParts[1]}`;
      setExpandedQuestions((prev) => new Set(prev).add(parentGroupId));
    }
    setExpandedQuestions((prev) => new Set(prev).add(question.id));

    setTimeout(() => {
      const element = document.getElementById(
        `question-container-${question.id}`
      );
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.style.transition = 'background-color 0.3s ease-in-out';
        element.style.backgroundColor = '#dbeafe';
        setTimeout(() => {
          element.style.backgroundColor = '';
        }, 1500);
      }
    }, 100);
  };

  const handleSubmit = () => {
    setValidationError(null);
    const visibleQuestions = nonGroupQuestions.filter((q) =>
      isQuestionVisible(q, currentResponses)
    );
    const unanswered = visibleQuestions.filter(
      (q) => !isQuestionAnswered(q.id, currentResponses)
    );

    if (unanswered.length > 0) {
      setValidationError(unanswered);
      setValidationErrorVisible(true);
      setExpandedQuestions(new Set(unanswered.map((q) => q.id)));
      const firstUnansweredSection = unanswered[0]?.category;
      if (firstUnansweredSection) setActiveSectionTitle(firstUnansweredSection);
      return;
    }
    setConfirmModalOpen(true);
  };

  const handleConfirmSubmit = () => {
    onSubmit();
    setConfirmModalOpen(false);
  };

  const parseAndRenderDescription = (text) => {
    if (!text) return null;
    const regex = /\[\[(.*?)\|(.*?)\]\]/g;
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (index % 3 === 1) {
        const questionId = parts[index + 1];
        const question = allQuestions.find((q) => q.id === questionId);
        if (!question) return part;

        return (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              handleJumpToQuestion(question);
            }}
            className="text-blue-600 font-semibold underline hover:text-blue-800 transition-colors"
          >
            {part}
          </button>
        );
      }
      if (index % 3 === 2) {
        return null;
      }
      return <span key={index}>{part}</span>;
    });
  };

  const renderQuestion = (question) => {
    if (!isQuestionVisible(question, currentResponses)) return null;

    const { id, type, text, options, explanation } = question;
    const value =
      currentResponses[id] || (type === 'multi_select_mcq' ? [] : '');
    const answered = isQuestionAnswered(id, currentResponses);
    const isExpanded = expandedQuestions.has(id);

    return (
      <div
        id={`question-container-${id}`}
        key={id}
        className={`bg-slate-50 rounded-xl border transition-all duration-300 ${
          isSubmitted
            ? 'border-slate-200 bg-slate-100'
            : answered
            ? 'border-green-200 bg-green-50'
            : 'border-slate-200'
        }`}
      >
        <button
          onClick={() => toggleQuestionExpansion(id)}
          className="w-full p-4 text-left hover:bg-slate-100 rounded-xl transition-colors duration-200 disabled:cursor-not-allowed"
          disabled={isSubmitted}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 flex items-center gap-3">
              <span className="text-lg mt-1">{answered ? '✅' : '⭕'}</span>
              <div className="bg-slate-200 rounded-md px-2.5 py-1">
                <span className="text-sm font-bold text-slate-600 font-mono">
                  {id}
                </span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-slate-900 font-medium leading-relaxed pt-1">
                {text}
              </p>
              {answered && !isExpanded && (
                <p
                  className={`text-sm mt-2 truncate ${
                    isSubmitted ? 'text-slate-600' : 'text-green-600'
                  }`}
                >
                  <strong>Answer:</strong>{' '}
                  {Array.isArray(value)
                    ? value.join(', ')
                    : String(value).substring(0, 100)}
                  ...
                </p>
              )}
            </div>
            <div className="flex-shrink-0 mt-1">
              {isExpanded ? (
                <ChevronUp size={20} className="text-slate-400" />
              ) : (
                <ChevronDown size={20} className="text-slate-400" />
              )}
            </div>
          </div>
        </button>
        {isExpanded && (
          <div className="px-4 pb-4 animate-in slide-in-from-top-2 duration-200">
            <div className="pl-16 space-y-3">
              {explanation && (
                <div className="p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg text-sm mb-3">
                  <p className="font-semibold mb-2">Classification Criteria:</p>
                  <p className="whitespace-pre-wrap leading-relaxed">
                    {explanation}
                  </p>
                </div>
              )}
              {type === 'long_answer' && (
                <textarea
                  value={value}
                  onChange={(e) => handleInputChange(id, e.target.value)}
                  placeholder="Enter detailed response..."
                  rows={4}
                  className="w-full p-3 border border-slate-300 rounded-lg resize-vertical focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-200"
                  disabled={isSubmitted}
                />
              )}
              {type === 'mcq' && (
                <div className="space-y-3">
                  {options.map((option, index) => (
                    <label
                      key={index}
                      className={`flex items-start gap-3 p-3 border rounded-lg transition-all duration-200 ${
                        isSubmitted
                          ? 'cursor-not-allowed'
                          : 'cursor-pointer hover:bg-slate-50'
                      } ${
                        value === option
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`q-${id}`}
                        value={option}
                        checked={value === option}
                        onChange={(e) => handleInputChange(id, e.target.value)}
                        className="mt-1 w-4 h-4 text-blue-600 focus:ring-blue-500"
                        disabled={isSubmitted}
                      />
                      <span className="text-sm leading-relaxed break-words">
                        {option}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {type === 'multi_select_mcq' && (
                <div className="space-y-3">
                  {options.map((option, index) => (
                    <label
                      key={index}
                      className={`flex items-start gap-3 p-3 border rounded-lg transition-all duration-200 ${
                        isSubmitted
                          ? 'cursor-not-allowed'
                          : 'cursor-pointer hover:bg-slate-50'
                      } ${
                        (value || []).includes(option)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        value={option}
                        checked={(value || []).includes(option)}
                        onChange={(e) => handleInputChange(id, e.target.value)}
                        className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        disabled={isSubmitted}
                      />
                      <span className="text-sm leading-relaxed break-words">
                        {option}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderQuestionNode = (q) => {
    if (!isQuestionVisible(q, currentResponses)) return null;

    if (q.type === 'group') {
      const isExpanded = expandedQuestions.has(q.id);
      const visibleSubQuestions = q.sub_questions.filter((subQ) =>
        isQuestionVisible(subQ, currentResponses)
      );
      const allSubsAnswered =
        visibleSubQuestions.length > 0 &&
        visibleSubQuestions.every((subQ) =>
          isQuestionAnswered(subQ.id, currentResponses)
        );

      return (
        <div
          id={`question-container-${q.id}`}
          key={q.id}
          className={`rounded-xl border transition-all duration-300 ${
            allSubsAnswered
              ? 'bg-green-50 border-green-200'
              : 'bg-white border-slate-200'
          }`}
        >
          <button
            onClick={() => toggleQuestionExpansion(q.id)}
            className="w-full p-4 text-left hover:bg-slate-100 rounded-t-xl transition-colors duration-200"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 flex items-center gap-3">
                <span className="text-lg mt-1">
                  {allSubsAnswered ? '✅' : '⭕'}
                </span>
                <div className="bg-slate-200 rounded-md px-2.5 py-1">
                  <span className="text-sm font-bold text-slate-600 font-mono">
                    {q.id}
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-slate-800 pt-1">{q.title}</h4>
                {allSubsAnswered && !isExpanded && (
                  <p className="text-sm mt-2 text-green-600">
                    <strong>Completed:</strong> All sub-questions answered.
                  </p>
                )}
              </div>
              <div className="flex-shrink-0 mt-1">
                {isExpanded ? (
                  <ChevronUp size={20} className="text-slate-400" />
                ) : (
                  <ChevronDown size={20} className="text-slate-400" />
                )}
              </div>
            </div>
          </button>
          {isExpanded && (
            <div className="p-4 pt-2 animate-in slide-in-from-top-2 duration-200">
              <div className="ml-16 space-y-4">
                {q.description && (
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">
                    {q.description}
                  </p>
                )}
                {q.context && (
                  <p className="text-xs text-slate-500 mt-2 whitespace-pre-wrap italic">
                    {q.context}
                  </p>
                )}
                <div className="mt-4 space-y-4 border-t border-slate-200 pt-4">
                  {q.sub_questions.map(renderQuestionNode)}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return renderQuestion(q);
  };

  const globalProgress = useMemo(() => {
    // Ticket 05: progress now uses the DSL-aware visibility check against
    // the flattened question list. Same shape of return as the old helper.
    const visible = nonGroupQuestions.filter((q) => isQuestionVisible(q, currentResponses));
    const answered = visible.filter((q) => isQuestionAnswered(q.id, currentResponses));
    const total = visible.length;
    const count = answered.length;
    return {
      answered: count,
      total,
      percentage: total > 0 ? (count / total) * 100 : 0,
    };
  }, [currentResponses, nonGroupQuestions]);

  const isComplete = globalProgress.percentage >= 100;

  const activeSection = questionnaireData.questionnaire.find(
    (s) => s.section_title === activeSectionTitle
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* --- MODIFICATION: Conditionally render header and progress --- */}
      {!isShortScreen && (
        <div className="bg-white p-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <h3 className="text-slate-900 text-lg font-semibold">
                📝 Research Paper Questionnaire
              </h3>
              {!isSubmitted && (
                <div className="flex flex-col items-end gap-2">
                  <div
                    className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      autoSaveStatus === 'saving'
                        ? 'bg-yellow-100 text-yellow-800'
                        : autoSaveStatus === 'saved'
                        ? 'bg-green-100 text-green-800'
                        : 'opacity-0'
                    }`}
                  >
                    {autoSaveStatus === 'saving'
                      ? 'Saving...'
                      : '✓ All changes saved'}
                  </div>
                  {autosaveError && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      {autosaveError}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">
                  Overall Progress
                </span>
                <span className="text-sm font-semibold text-blue-600">
                  {globalProgress.answered} / {globalProgress.total}
                </span>
              </div>
              <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${globalProgress.percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border-b border-slate-200 px-4 flex-shrink-0">
        {/* --- MODIFICATION: Conditionally render tabs or a dropdown --- */}
        {isShortScreen ? (
          <div className="py-2">
            <select
              value={activeSectionTitle}
              onChange={(e) => setActiveSectionTitle(e.target.value)}
              className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-800 font-medium focus-academic"
            >
              {questionnaireData.questionnaire.map((section) => {
                const questionsInSection = allQuestions.filter(
                  (q) =>
                    q.category === section.section_title && q.type !== 'group'
                );
                const visibleQuestionsInSection = questionsInSection.filter(
                  (q) => isQuestionVisible(q, currentResponses)
                );
                const answeredInSection = visibleQuestionsInSection.filter(
                  (q) => isQuestionAnswered(q.id, currentResponses)
                ).length;
                const totalInSection = visibleQuestionsInSection.length;
                return (
                  <option
                    key={section.section_title}
                    value={section.section_title}
                  >
                    {section.section_title} ({answeredInSection}/
                    {totalInSection})
                  </option>
                );
              })}
            </select>
          </div>
        ) : (
          <div className="flex gap-1 overflow-x-auto">
            {questionnaireData.questionnaire.map((section) => {
              const questionsInSection = allQuestions.filter(
                (q) =>
                  q.category === section.section_title && q.type !== 'group'
              );
              const visibleQuestionsInSection = questionsInSection.filter((q) =>
                isQuestionVisible(q, currentResponses)
              );
              const answeredInSection = visibleQuestionsInSection.filter((q) =>
                isQuestionAnswered(q.id, currentResponses)
              ).length;
              const totalInSection = visibleQuestionsInSection.length;
              const isSectionComplete =
                totalInSection > 0 && answeredInSection === totalInSection;

              let tabClass =
                'px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 ';

              if (activeSectionTitle === section.section_title) {
                tabClass += isSectionComplete
                  ? 'border-green-500 text-green-700 bg-green-50 font-semibold'
                  : 'border-blue-500 text-blue-600 bg-blue-50 font-semibold';
              } else {
                tabClass += isSectionComplete
                  ? 'border-transparent text-green-600 hover:bg-green-50'
                  : 'border-transparent text-slate-600 hover:bg-slate-50';
              }

              return (
                <button
                  key={section.section_title}
                  className={tabClass}
                  onClick={() => setActiveSectionTitle(section.section_title)}
                >
                  {section.section_title} ({answeredInSection}/{totalInSection})
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto custom-scrollbar p-4"
      >
        {isSubmitted ? (
          <div className="p-4 bg-green-50 text-green-800 border-l-4 border-green-500 rounded-r-lg flex items-center gap-3">
            <Lock size={24} />
            <div>
              <h4 className="font-bold text-lg">Review Submitted</h4>
              <p>Your responses are locked and cannot be edited.</p>
            </div>
          </div>
        ) : validationError && isValidationErrorVisible ? (
          <div className="relative p-4 bg-red-50 text-red-800 border-l-4 border-red-500 rounded-r-lg mb-4">
            <button
              onClick={() => setValidationErrorVisible(false)}
              className="absolute top-2 right-2 p-1 hover:bg-red-200 rounded-full"
              aria-label="Close validation message"
            >
              <X size={18} className="text-red-700" />
            </button>
            <h4 className="font-bold">Incomplete Submission</h4>
            <p className="mb-2 pr-6">
              Please answer all visible questions. Click on a question ID to
              jump to it:
            </p>
            <div className="flex flex-wrap gap-2">
              {validationError.map((q) => (
                <button
                  key={q.id}
                  onClick={() => handleJumpToQuestion(q)}
                  className="px-2 py-1 bg-red-100 text-red-900 border border-red-300 rounded-md hover:bg-red-200 transition-colors text-sm font-mono"
                >
                  Q{q.id}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="space-y-4 mt-4">
          {activeSection && (
            <div className="space-y-4">
              {activeSection.section_description && (
                <div className="p-4 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg">
                  <p className="whitespace-pre-wrap leading-relaxed">
                    {parseAndRenderDescription(
                      activeSection.section_description
                    )}
                  </p>
                </div>
              )}
              {activeSection.questions.map(renderQuestionNode)}
            </div>
          )}
        </div>
      </div>
      <div className="bg-white border-t border-slate-200 p-4 flex-shrink-0">
        {/* --- MODIFICATION: Conditionally render compact or full-size buttons --- */}
        {isShortScreen ? (
          <div className="flex gap-2">
            {!isSubmitted && (
              <div className="flex gap-2">
                <Tooltip text="Save Progress">
                  <button
                    className="flex items-center justify-center p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    onClick={handleSave}
                  >
                    <Save size={16} />
                  </button>
                </Tooltip>
                <Tooltip text="Reset Form">
                  <button
                    className="flex items-center justify-center p-2 bg-slate-100 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-200"
                    onClick={handleReset}
                  >
                    <RotateCcw size={16} />
                  </button>
                </Tooltip>
              </div>
            )}
            <Tooltip text="View Summary">
              <button
                className="flex items-center justify-center p-2 bg-slate-100 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-200"
                onClick={() => setShowSummary(true)}
              >
                <FileText size={16} />
              </button>
            </Tooltip>
            {!isSubmitted && (
              <Tooltip
                text={
                  isComplete
                    ? 'Submit your final, uneditable review.'
                    : 'You must answer all questions before submitting.'
                }
              >
                <button
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed"
                  onClick={handleSubmit}
                  disabled={!isComplete}
                >
                  <Send size={16} />
                  <span className="text-sm font-semibold">Submit</span>
                </button>
              </Tooltip>
            )}
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2">
            {!isSubmitted && (
              <>
                <button
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-1"
                  onClick={handleSave}
                >
                  <Save size={16} /> Save Progress
                </button>
                <Tooltip text="Clears all answers for this paper. This action cannot be undone.">
                  <button
                    className="flex w-full items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-200 flex-1"
                    onClick={handleReset}
                  >
                    <RotateCcw size={16} /> Reset
                  </button>
                </Tooltip>
                <Tooltip
                  text={
                    isComplete
                      ? 'Submit your final, uneditable review.'
                      : 'You must answer all questions before submitting.'
                  }
                >
                  <button
                    className="flex w-full items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex-1 disabled:bg-green-300 disabled:cursor-not-allowed"
                    onClick={handleSubmit}
                    disabled={!isComplete}
                  >
                    <Send size={16} /> Submit Final Review
                  </button>
                </Tooltip>
              </>
            )}
            <button
              className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-200 flex-1"
              onClick={() => setShowSummary(true)}
            >
              <FileText size={16} /> View Summary
            </button>
          </div>
        )}
      </div>
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmSubmit}
        title="Finalize Submission?"
        message="You are about to submit your review. After submission, your responses will be locked and cannot be edited. Are you sure you want to proceed?"
      />
      {showSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl max-h-[80vh] w-full flex flex-col shadow-2xl">
            <div className="flex justify-between items-center p-6 border-b border-slate-200 flex-shrink-0">
              <h4 className="text-slate-900 font-semibold text-lg">
                📋 Response Summary
              </h4>
              <button
                className="p-2 hover:bg-slate-200 rounded-full"
                onClick={() => setShowSummary(false)}
              >
                <span className="text-2xl text-slate-500 leading-none">×</span>
              </button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar">
              {nonGroupQuestions
                .filter((q) => isQuestionVisible(q, currentResponses))
                .map((q) => {
                  if (isQuestionAnswered(q.id, currentResponses)) {
                    const response = currentResponses[q.id];
                    return (
                      <div
                        key={q.id}
                        className="mb-6 pb-4 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium text-slate-900 mb-2">
                          <strong>Q{q.id}:</strong> {q.text}
                        </div>
                        <div className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">
                          {Array.isArray(response)
                            ? response.join(', ')
                            : response}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionForm;
